import { useEffect, useMemo, useState } from "react";
import { Button, Upload, type UploadProps, type UploadUserFile } from "zhihao-ui-react";
import type { File as FileEntity } from "@zhihaoscm/entity";
import { fileService } from "@/api/services/fileService";
import { waybillService } from "@/api/services/waybillService";

export interface SingleFileUploadProps {
    modelValue?: number;
    file?: FileEntity;
    size?: number;
    icon?: boolean;
    tip?: string;
    acceptType?: string[];
    limitFileTypes?: string[];
    typesErrorMessage?: string;
    type?: number;
    isNotShowLoading?: boolean;
    canDelete?: boolean;
    uploadUsername?: string;
    businessId?: string;
    isNeedWatermark?: boolean;
    onUpdateModelValue?: (value: number | undefined) => void;
    onUpdateFile?: (value: FileEntity | undefined) => void;
    onDelete?: (value: number | undefined) => void;
    validateFile?: () => void;
}

/** 单文件上传公共实现，兼容旧版 modelValue/file 双向数据和预览删除入口。 */
export default function SingleFileUpload({
    modelValue,
    file,
    size = 10,
    icon = false,
    tip = "上传",
    acceptType,
    limitFileTypes,
    typesErrorMessage,
    type = 1,
    canDelete = true,
    businessId,
    isNeedWatermark = false,
    onUpdateModelValue,
    onUpdateFile,
    onDelete,
    validateFile
}: SingleFileUploadProps) {
    const [entity, setEntity] = useState<FileEntity | undefined>(file);
    const accepted = limitFileTypes ?? acceptType ?? ["pdf", "ofd", "jpeg", "png", "xml", "xls", "xlsx"];
    const fileList = useMemo<UploadUserFile[]>(
        () => (entity ? [{ name: entity.name, url: entity.path, response: entity }] : []),
        [entity]
    );
    useEffect(() => {
        if (!modelValue) {
            setEntity(undefined);
            onUpdateFile?.(undefined);
            return;
        }
        if (entity?.id === modelValue) return;
        fileService.getFileById(modelValue).then(next => {
            setEntity(next);
            onUpdateFile?.(next);
        });
    }, [modelValue]);
    const beforeUpload: UploadProps["beforeUpload"] = rawFile => {
        const suffix = rawFile.name.split(".").pop()?.toLowerCase() || "";
        const validType = accepted.some(
            item =>
                item.toLowerCase().replace(/^\./, "") === suffix || item.toLowerCase() === rawFile.type.toLowerCase()
        );
        if (!validType || rawFile.size / 1024 / 1024 > size) {
            window.dispatchEvent(
                new CustomEvent("zh-upload-error", {
                    detail: typesErrorMessage || `仅支持 ${accepted.join(",")} 文件且大小不超过 ${size}M`
                })
            );
            return false;
        }
        return true;
    };
    const httpRequest: UploadProps["httpRequest"] = async options => {
        const formData = new FormData();
        formData.append("file", options.file);
        formData.append("fileName", options.file.name);
        const next =
            businessId && isNeedWatermark
                ? ((await waybillService.uploadWatermark(businessId, formData)) as FileEntity)
                : await fileService.uploadFile(formData);
        setEntity(next);
        onUpdateFile?.(next);
        onUpdateModelValue?.(next.id);
        validateFile?.();
        options.onSuccess(next);
    };
    const remove = () => {
        setEntity(undefined);
        onUpdateFile?.(undefined);
        onUpdateModelValue?.(undefined);
        onDelete?.(modelValue);
    };
    const preview = () => entity?.path && window.open(entity.path, "_blank", "noopener,noreferrer");
    return (
        <Upload
            action="#"
            accept={accepted.map(item => (item.startsWith(".") ? item : `.${item}`)).join(",")}
            limit={1}
            fileList={fileList}
            showFileList={false}
            beforeUpload={beforeUpload}
            httpRequest={httpRequest}
        >
            {entity ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span>{entity.name}</span>
                    <Button
                        size="small"
                        onClick={event => {
                            event.stopPropagation();
                            preview();
                        }}
                    >
                        查看/下载
                    </Button>
                    {(canDelete || type === 1) && (
                        <Button
                            size="small"
                            onClick={event => {
                                event.stopPropagation();
                                remove();
                            }}
                        >
                            删除
                        </Button>
                    )}
                </span>
            ) : (
                <Button type="link">
                    {icon ? (
                        <span aria-hidden="true" className="iconfont icon-a-Frame30" style={{ marginRight: 5 }} />
                    ) : null}
                    {tip}
                </Button>
            )}
        </Upload>
    );
}
